/**
 * @fileoverview First-load location prompt.
 * @description Location isn't required for the whole session — the app asks for it once, as it
 * first loads. Calls location.store.ts's start() once, on mount; if location isn't already on, the
 * store opens the prompt and asks the browser at the same moment, and this renders
 * LocationPromptDialog over the app (kept `inert` underneath) until its Enable button confirms
 * location is on. After that the prompt never comes back during this load: if location is turned
 * off later, requests simply go out without coordinates.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

import { useEffect } from 'react'

import LocationPromptDialog from '@/shared/components/molecules/LocationPromptDialog'
import { useLocationStore } from '@/shared/lib/device/location.store'

import type { ReactNode } from 'react'

interface LocationGateProps {
  children: ReactNode
}

/**
 * @component
 * @param {LocationGateProps} props - The app content.
 * @param {ReactNode} props.children - AppProviders — always rendered; the prompt sits on top.
 */
function LocationGate({ children }: LocationGateProps) {
  const prompt = useLocationStore((state) => state.prompt)
  const reason = useLocationStore((state) => state.reason)
  const isConfirming = useLocationStore((state) => state.isConfirming)
  const start = useLocationStore((state) => state.start)
  const confirmEnabled = useLocationStore((state) => state.confirmEnabled)

  useEffect(() => {
    start()
  }, [start])

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        useLocationStore.getState().recheckPermission()
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange)
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
  }, [])

  const isPromptOpen = prompt === 'open'

  return (
    <>
      <div inert={isPromptOpen}>{children}</div>
      {isPromptOpen && (
        <LocationPromptDialog
          reason={reason}
          isConfirming={isConfirming}
          onEnable={() => void confirmEnabled()}
        />
      )}
    </>
  )
}

export default LocationGate
